import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, TextField, Button, Paper, LinearProgress, List, ListItemButton, ListItemText, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { authHeader } from "../auth";

// Poste de saisie des pénalités de qualification télémark — UN juge, UNE
// catégorie (style OU jump), UNE parité de dossard à la fois. Dispositif
// "beau temps" (27.08.2026) : jusqu'à 2 juges style + 1 juge jump PAR
// parité (pair/impair), soit jusqu'à 6 postes indépendants pour un même
// event_key. Leurs saisies s'ADDITIONNENT (voir telemarkResultsRouter.js)
// — avec un seul juge style présent (configuration réduite), le total
// vaut simplement sa saisie.
//
// URL : /saisiepenalite/:eventKey/:category/:parity/:slot
//   category : "style" | "jump"
//   parity   : "pair" | "impair"  (déduit du numéro de dossard, ex 12 -> pair)
//   slot     : "1" | "2"          (poste de juge — 2 seulement utilisé pour style)
//
// Mêmes principes que la version précédente : les 3 prochains dossards
// (de la parité assignée à CE poste) sont affichés en cartes
// indépendantes, AUCUNE activation automatique de case (pas de focus, pas
// de minuteur) — le juge touche la carte qu'il veut, quand il veut.

const POLL_MS = 5000;
const VISIBLE_COUNT = 3;

const CATEGORY_LABEL = { style: "Style", jump: "Jump" };
const PARITY_LABEL = { pair: "dossards pairs", impair: "dossards impairs" };

function athleteName(a) {
    return `${a.lastname || ""} ${a.firstname || ""}`.trim();
}

function isMatchingParity(bib, parity) {
    const n = Number(bib);
    if (!Number.isFinite(n)) return false;
    const isEven = n % 2 === 0;
    return parity === "pair" ? isEven : !isEven;
}

export default function SaisiePenalite() {
    const { eventKey, category, parity, slot } = useParams();
    const numSlot = Number(slot);

    const [startlist, setStartlist] = useState([]);
    const [entries, setEntries] = useState([]); // TOUTES les entrées (tous postes) — pour savoir ce que CE poste a déjà saisi
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [selectedBib, setSelectedBib] = useState(null); // pour corriger une saisie déjà confirmée PAR CE poste
    const [savingBib, setSavingBib] = useState(null);

    // Valeurs de saisie EN COURS, par dossard — indépendantes, jamais
    // réinitialisées/déplacées automatiquement.
    const [values, setValues] = useState({}); // bib -> string
    const [correctionValue, setCorrectionValue] = useState("");

    const apiFetch = useCallback(
        (url, options = {}) =>
            fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            }),
        []
    );

    const loadStartlist = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/timing/startlist/${encodeURIComponent(eventKey)}`);
            if (res.ok) setStartlist(await res.json());
        } catch (err) {
            setError(err.message);
        }
    }, [apiFetch, eventKey]);

    const loadEntries = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/timing/penalty-entries/${encodeURIComponent(eventKey)}`);
            if (res.ok) setEntries(await res.json());
        } catch (err) {
            setError(err.message);
        }
    }, [apiFetch, eventKey]);

    useEffect(() => {
        loadStartlist();
        loadEntries();
        const t = setInterval(() => {
            loadStartlist();
            loadEntries();
        }, POLL_MS);
        return () => clearInterval(t);
    }, [loadStartlist, loadEntries]);

    // Ce que CE poste précis (category + slot) a déjà saisi, indexé par
    // dossard — les entrées des AUTRES postes (l'autre juge style, le
    // juge jump...) n'apparaissent pas ici : chaque poste ne voit que sa
    // propre progression.
    const mine = useMemo(() => {
        const map = {};
        entries
            .filter((e) => e.category === category && Number(e.slot) === numSlot)
            .forEach((e) => {
                map[String(e.bib)] = e;
            });
        return map;
    }, [entries, category, numSlot]);

    const assigned = useMemo(
        () => startlist.filter((a) => isMatchingParity(a.bib, parity)),
        [startlist, parity]
    );
    const pending = useMemo(() => assigned.filter((a) => !mine[String(a.bib)]), [assigned, mine]);
    const done = useMemo(() => assigned.filter((a) => mine[String(a.bib)]), [assigned, mine]);

    const visiblePending = pending.slice(0, VISIBLE_COUNT);
    const visibleBibsKey = visiblePending.map((a) => String(a.bib)).join(",");

    useEffect(() => {
        setValues((prev) => {
            let changed = false;
            const next = { ...prev };
            visiblePending.forEach((a) => {
                const bib = String(a.bib);
                if (!(bib in next)) {
                    next[bib] = "";
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleBibsKey]);

    const saveEntry = async (bib, valueStr) => {
        const value = Number(String(valueStr).replace(",", ".")) || 0;
        if (value < 0) {
            setError("La pénalité doit être positive.");
            return false;
        }
        setSavingBib(bib);
        setError("");
        setInfo("");
        try {
            const res = await apiFetch(
                `/api/timing/penalty-entries/${encodeURIComponent(eventKey)}/${encodeURIComponent(bib)}/${category}/${numSlot}`,
                { method: "POST", body: JSON.stringify({ value }) }
            );
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return false;
            }
            setEntries((prev) => [
                ...prev.filter((e) => !(String(e.bib) === bib && e.category === category && Number(e.slot) === numSlot)),
                { bib, category, slot: numSlot, value, updatedAt: new Date().toISOString() },
            ]);
            setInfo(`Dossard ${bib} confirmé ✓`);
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setSavingBib(null);
        }
    };

    const confirmPending = async (bib) => {
        const ok = await saveEntry(bib, values[bib] ?? "");
        if (ok) {
            setValues((prev) => {
                const next = { ...prev };
                delete next[bib];
                return next;
            });
        }
    };

    const openCorrection = (bib) => {
        const existing = mine[String(bib)];
        setCorrectionValue(existing ? String(existing.value) : "");
        setSelectedBib(bib);
    };

    const confirmCorrection = async () => {
        if (!selectedBib) return;
        const ok = await saveEntry(String(selectedBib), correctionValue);
        if (ok) setSelectedBib(null);
    };

    const correctionAthlete = selectedBib
        ? startlist.find((a) => String(a.bib) === String(selectedBib))
        : null;

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", p: 2, pb: 6 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.25 }}>
                {CATEGORY_LABEL[category] || category} — {PARITY_LABEL[parity] || parity} — Juge {numSlot}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {eventKey}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {done.length} / {assigned.length} confirmés (ce poste)
            </Typography>
            <LinearProgress
                variant="determinate"
                value={assigned.length ? (done.length / assigned.length) * 100 : 0}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {error}
                </Typography>
            )}
            {info && !error && (
                <Typography color="success.main" variant="body2" sx={{ mb: 1 }}>
                    {info}
                </Typography>
            )}

            {selectedBib && correctionAthlete && (
                <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }} elevation={3}>
                    <Chip label="Correction" size="small" color="warning" sx={{ mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 0.5 }}>
                        [{correctionAthlete.bib}]
                    </Typography>
                    <Typography variant="subtitle1" sx={{ textAlign: "center", mb: 2 }}>
                        {athleteName(correctionAthlete) || "—"}
                    </Typography>

                    <TextField
                        label={`${CATEGORY_LABEL[category] || category} (s)`}
                        type="tel"
                        inputMode="decimal"
                        value={correctionValue}
                        onChange={(e) => setCorrectionValue(e.target.value)}
                        fullWidth
                        sx={{ mb: 2, "& input": { fontSize: 28, textAlign: "center", py: 1.5 } }}
                    />

                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        fullWidth
                        onClick={confirmCorrection}
                        disabled={savingBib === String(selectedBib)}
                        sx={{ py: 1.5, fontWeight: "bold" }}
                    >
                        {savingBib === String(selectedBib) ? "Enregistrement…" : "Enregistrer la correction ✓"}
                    </Button>
                    <Button fullWidth sx={{ mt: 1 }} onClick={() => setSelectedBib(null)}>
                        Annuler
                    </Button>
                </Paper>
            )}

            {!selectedBib && visiblePending.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                    {visiblePending.map((a, idx) => {
                        const bib = String(a.bib);
                        return (
                            <Paper key={bib} sx={{ p: 2, borderRadius: 3 }} elevation={idx === 0 ? 3 : 1}>
                                <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 0.25 }}>
                                    [{a.bib}]
                                </Typography>
                                <Typography variant="subtitle1" sx={{ textAlign: "center", mb: 1.5 }}>
                                    {athleteName(a) || "—"}
                                </Typography>

                                <TextField
                                    label={`${CATEGORY_LABEL[category] || category} (s)`}
                                    type="tel"
                                    inputMode="decimal"
                                    value={values[bib] ?? ""}
                                    onChange={(e) => setValues((prev) => ({ ...prev, [bib]: e.target.value }))}
                                    fullWidth
                                    sx={{ mb: 1.5, "& input": { fontSize: 26, textAlign: "center", py: 1.3 } }}
                                />

                                <Button
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                    onClick={() => confirmPending(bib)}
                                    disabled={savingBib === bib}
                                    sx={{ py: 1.3, fontWeight: "bold" }}
                                >
                                    {savingBib === bib ? "Enregistrement…" : "Confirmer ✓"}
                                </Button>
                            </Paper>
                        );
                    })}
                </Box>
            )}

            {!selectedBib && visiblePending.length === 0 && (
                <Paper sx={{ p: 3, mb: 3, textAlign: "center", borderRadius: 3 }} elevation={0} variant="outlined">
                    <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography>Tous les dossards assignés à ce poste sont saisis.</Typography>
                </Paper>
            )}

            {done.length > 0 && (
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Déjà confirmés (ce poste) — toucher pour corriger
                    </Typography>
                    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                        <List dense disablePadding>
                            {done.map((a) => {
                                const e = mine[String(a.bib)];
                                return (
                                    <ListItemButton
                                        key={a.bib}
                                        selected={String(selectedBib) === String(a.bib)}
                                        onClick={() => openCorrection(a.bib)}
                                    >
                                        <ListItemText
                                            primary={`[${a.bib}] ${athleteName(a) || "—"}`}
                                            secondary={`${CATEGORY_LABEL[category] || category} : ${e?.value ?? 0}s`}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Paper>
                </>
            )}
        </Box>
    );
}